/**
 * The error taxonomy. See docs/ARCHITECTURE.md section 11.
 *
 * Only the classes needed at M1 are defined. The rest (AuthError,
 * PermissionError, NetworkError, ValidationError, DataIntegrityError) arrive
 * with the layers that raise them.
 *
 * Calculation diagnostics are deliberately NOT errors. A missing rate is a
 * value the UI has to render, not an exception. See docs/BUSINESS_RULES.md
 * section 11.1.
 */

/** Base class so callers can distinguish our errors from anything else. */
export class AppError extends Error {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, options)
    this.name = new.target.name
  }
}

/**
 * A required environment variable is missing or malformed.
 *
 * Thrown at the point of use rather than at module load, so that the
 * application still boots during M1 to M3 when Supabase is not yet connected.
 */
export class EnvironmentConfigurationError extends AppError {
  readonly variableNames: readonly string[]

  constructor(message: string, variableNames: readonly string[]) {
    super(message)
    this.variableNames = variableNames
  }
}

/**
 * The Supabase client was requested before Supabase was configured. Expected
 * during M1 to M3. In a production build this is a hard startup failure
 * instead, raised by assertEnvironmentIsUsable().
 */
export class SupabaseNotConfiguredError extends EnvironmentConfigurationError {}
